<template>
  <figure class="highcharts-figure">
    <div :id="containerId"></div>
  </figure>
</template>

<script setup>
import { watch, nextTick } from 'vue'

import Highcharts from 'highcharts'
import * as Sentry from '@sentry/vue'
import { useSessionValues } from '@/composables/useSessionValues'

const props = defineProps({
  containerId: {
    type: String,
    required: true,
  },
  series: {
    type: Array,
    default: () => [],
  },
  colors: {
    type: Array,
    default: () => [],
  },
  total: {
    type: [Number, String],
    default: 0,
  },
})

const { language } = useSessionValues()
const drawChart = (containerId, series, colors, total) => {
  if (!containerId || !series) return

  try {
    nextTick(() => {
      if (!document.getElementById(containerId)) return
      Highcharts.Legend.prototype.setItemEvents = function () {}
      Highcharts.chart(containerId, {
        title: null,
        chart: {
          animation: false,
          type: 'pie',
          height: 217,
          plotBorderWidth: 0,
          style: {
            fontFamily: `SourceSansPro, Sans-Serif, "Sans Serif"`,
          },
          events: {
            load: function () {
              const chart = this
              const series = chart.series[0]
              const centerX = series.center[0] + chart.plotLeft
              const centerY = series.center[1] + chart.plotTop

              chart.renderer
                .text(total.toLocaleString(language.value), centerX, centerY + 5)
                .css({
                  fontSize: '24px',
                  color: '#000',
                })
                .attr({
                  align: 'center',
                })
                .add()
            },
          },
        },
        credits: {
          enabled: false,
        },
        series: [
          {
            name: 'Distribution',
            data: series,
            colorByPoint: true,
            colors: colors,
          },
        ],
        plotOptions: {
          pie: {
            showInLegend: true,
            cursor: 'pointer',
            borderWidth: 0,
            innerSize: '75%',
            borderRadius: 0,
            animation: false,
            dataLabels: {
              enabled: false,
            },
            allowPointSelect: false,
            states: {
              hover: {
                enabled: false,
              },
              inactive: {
                enabled: false,
              },
            },
          },
        },
        legend: {
          enabled: true,
          align: 'right',
          verticalAlign: 'middle',
          layout: 'vertical',
          itemStyle: {
            fontSize: '12px',
            cursor: 'default',
          },
          symbolRadius: 100,
          symbolHeight: 12,
          symbolWidth: 12,
          itemMarginBottom: 4,
          maxWidth: 150,
        },
        tooltip: {
          enabled: false,
        },
      })
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

watch(
  () => [props.series, props.colors, props.total],
  ([newSeries, newColors, newTotal]) => {
    if (newSeries?.length > 0) {
      drawChart(props.containerId, newSeries, newColors, newTotal)
    }
  },
  { deep: true, immediate: true }
)

watch(language, (currentValue, oldValue) => {
  if (currentValue !== oldValue) {
    drawChart(props.containerId, props.series, props.colors, props.total)
  }
})
</script>
